import React, { createContext, useContext, useState, useCallback } from 'react';

const DashboardDataContext = createContext(null);

export function DashboardDataProvider({ children }) {
  // Simpan data per-tipe, tidak saling overwrite
  const [dataStore, setDataStore] = useState({});
  // Track tipe yang sedang aktif
  const [activeType, setActiveType] = useState(null);

  const publishData = useCallback((data) => {
    if (!data?.type) return;
    console.log('[DashboardDataContext] publishData:', data.type,
      data.filtered?.length || 0, 'records');
    // Simpan per tipe — solman tidak menimpa sap_usage dan sebaliknya
    setDataStore(prev => ({ ...prev, [data.type]: data }));
    setActiveType(data.type);
  }, []);

  const setActive = useCallback((type) => {
    setActiveType(type);
  }, []);

  const clearData = useCallback(() => {
    setDataStore({});
    setActiveType(null);
  }, []);

  // dashboardData = data dari tipe yang sedang aktif
  const dashboardData = activeType ? dataStore[activeType] : null;

  return (
    <DashboardDataContext.Provider value={{
      dashboardData, dataStore, activeType,
      publishData, setActive, clearData
    }}>
      {children}
    </DashboardDataContext.Provider>
  );
}

export function useDashboardData() {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) throw new Error('useDashboardData harus dalam DashboardDataProvider');
  return ctx;
}

// ── Format SAP Usage ──────────────────────────────────────────────────────────
export function formatSAPDataForAI({ rawData, filtered, fileName, activeFilters }) {
  if (!filtered || filtered.length === 0) return null;

  const totalHours  = filtered.reduce((s, r) => s + parseFloat(r.TotalHours || 0), 0);
  const totalUsers  = new Set(filtered.map(r => r.User || r.UserID)).size;
  const activeUsers = filtered.filter(r => parseFloat(r.TotalHours || 0) > 0).length;

  const byCompany = {};
  filtered.forEach(r => { const co = r.Company||'?'; byCompany[co]=(byCompany[co]||0)+parseFloat(r.TotalHours||0); });

  const byModule = {};
  filtered.forEach(r => { const m = r.Module||'?'; byModule[m]=(byModule[m]||0)+parseFloat(r.TotalHours||0); });

  const userHours = {};
  filtered.forEach(r => { const u = r.User||r.UserID||'?'; userHours[u]=(userHours[u]||0)+parseFloat(r.TotalHours||0); });

  const topUsers = Object.entries(userHours).sort((a,b)=>b[1]-a[1]).slice(0,10)
    .map(([u,h])=>`${u}: ${h.toFixed(1)}h`);
  const inactiveUsers = Object.entries(userHours).filter(([,h])=>h===0).map(([u])=>u).slice(0,20);

  const companyStr = Object.entries(byCompany).sort((a,b)=>b[1]-a[1])
    .map(([c,h])=>`${c}: ${h.toFixed(1)}h`).join(', ');
  const moduleStr = Object.entries(byModule).sort((a,b)=>b[1]-a[1])
    .map(([m,h])=>`${m}: ${h.toFixed(1)}h`).join(', ');

  return `=== DATA SAP USER UTILIZATION (AKTUAL) ===
Sumber File  : ${fileName||'N/A'}
Filter Aktif : ${activeFilters||'Semua data'}
Total Records: ${filtered.length} dari ${rawData?.length||filtered.length} total

RINGKASAN:
- Total Jam   : ${totalHours.toFixed(1)} jam
- Total User  : ${totalUsers}
- User Aktif  : ${activeUsers}
- Tidak Aktif : ${inactiveUsers.length}

USAGE PER COMPANY: ${companyStr||'-'}

USAGE PER MODULE: ${moduleStr||'-'}

TOP 10 USER:
${topUsers.join('\n')||'-'}
${inactiveUsers.length>0?`\nUSER TIDAK AKTIF: ${inactiveUsers.join(', ')}`:''}
==========================================`.trim();
}

// ── Format Solman ─────────────────────────────────────────────────────────────
export function formatSolmanDataForAI({ filtered, stats, filters }) {
  if (!filtered?.length || !stats) return null;

  const { company='All', team='All', year='All', status='All' } = filters||{};

  const topTeams = (stats.byTeam||[]).slice(0,5)
    .map(t=>`${t.name}: ${t.value} tiket`).join(', ');
  const statusStr = (stats.byStatus||[])
    .map(([s,v])=>`${s}: ${v}`).join(', ');
  const priorityStr = (stats.byPriority||[])
    .map(p=>`${p.name}: ${p.value}`).join(', ');
  const recentMonths = (stats.byMonthStatus||[]).slice(-3)
    .map(m=>`${m.label}: ${m.total} (closed:${m.Closed||0})`).join(', ');
  const openByTeam = (stats.byTeamStatus||[]).filter(t=>t._total>0).slice(0,5)
    .map(t=>{
      const openCount=["Solution Proposed to User","Feedback Awaited - User",
        "In process - Support Team","Approval Awaited","Agreed by User"]
        .reduce((s,st)=>s+(t[st]||0),0);
      return `${t.team}: ${openCount} open/${t._total} total`;
    }).join(', ');

  return `=== DATA SAP SOLUTION MANAGER (AKTUAL) ===
Filter: Company=${company} · Team=${team} · Year=${year} · Status=${status}
Total : ${filtered.length} tiket

RINGKASAN:
- Total  : ${stats.total}
- Open   : ${stats.open} (${stats.total?Math.round(stats.open/stats.total*100):0}%)
- Closed : ${stats.closed} (${stats.total?Math.round(stats.closed/stats.total*100):0}%)
- Withdrawn: ${stats.withdrawn}

STATUS: ${statusStr||'-'}
PRIORITAS: ${priorityStr||'-'}
TOP 5 TEAM: ${topTeams||'-'}
OPEN PER TEAM: ${openByTeam||'-'}
TREN 3 BULAN: ${recentMonths||'-'}
==========================================`.trim();
}

// ── Auto-detect formatter ─────────────────────────────────────────────────────


// ── Format AWS Billing untuk AI ───────────────────────────────────────────────
export function formatAWSBillingDataForAI({ allData, totals, services, periods }) {
  if (!allData?.length) return null;

  const topServices = (services || []).slice(0, 10)
    .map(s => `${s.name}: $${s.total.toFixed(2)} (tax: $${s.tax.toFixed(2)})`)
    .join('\n');

  const recentPeriods = (periods || []).slice(-6).join(', ');
  const currency = allData[0]?.CurrencyCode || 'USD';

  return `=== DATA AWS BILLING DASHBOARD (AKTUAL) ===
Total Records : ${allData.length.toLocaleString()} rows
Periode       : ${periods?.length || 0} billing period(s)
Terbaru       : ${recentPeriods || '-'}

RINGKASAN BIAYA:
- Total Cost     : $${(totals?.total || 0).toFixed(2)} ${currency}
- Cost Before Tax: $${(totals?.cost  || 0).toFixed(2)}
- Tax Amount     : $${(totals?.tax   || 0).toFixed(2)}
- Credits        : $${Math.abs(totals?.credits || 0).toFixed(2)}

TOP 10 AWS SERVICES:
${topServices || '-'}
==========================================`.trim();
}

export function formatDataForAI(dashboardData) {
  if (!dashboardData) return null;
  if (dashboardData.type === 'sap_usage')    return formatSAPDataForAI(dashboardData);
  if (dashboardData.type === 'solman')       return formatSolmanDataForAI(dashboardData);
  if (dashboardData.type === 'aws_cost')     return formatAWSCostDataForAI(dashboardData);
  if (dashboardData.type === 'aws_billing')  return formatAWSBillingDataForAI(dashboardData);
  if (dashboardData.type === 'aws_inventory') return formatAWSInventoryDataForAI(dashboardData);
  return null;
}

// -- Format AWS Inventory untuk AI ---------------------------------------------
export function formatAWSInventoryDataForAI({ allData, filtered, summary, lastRefreshed, diff, createdHist, activeTab, query }) {
  if (!allData?.length) return null;
  const rows = (filtered?.length ? filtered : allData);
  const bySvc = {};
  allData.forEach(r => { bySvc[r.service] = (bySvc[r.service] || 0) + 1; });
  const svcStr = Object.entries(bySvc).sort((a, b) => b[1] - a[1]).map(([s, n]) => `${s}: ${n}`).join(', ');
  const line = (r) => [r.service, r.id, r.name && r.name !== r.id ? `"${r.name}"` : null,
    r.region, r.type, r.state, r.created ? `created=${r.created}` : null, r.tags].filter(Boolean).join(' | ');
  const MAX = 400;
  const list = rows.slice(0, MAX).map(line).join('\n');
  let diffStr = '-';
  if (diff && !diff.empty) {
    if (diff.baseline) diffStr = 'Snapshot pertama, belum ada pembanding.';
    else {
      const s = diff.summary || {};
      const items = (diff.items || []).slice(0, 100).map(i =>
        `${i.kind} | ${i.service} | ${i.resourceId || i.id || i.key || ''} ${(i.label || i.name) ? `"${i.label || i.name}"` : ''}${i.createdBy ? ` | dibuat oleh ${i.createdBy} (${i.createdEvent || '?'} @ ${i.createdAt || '?'})` : ''}${i.changes ? ` | ${JSON.stringify(i.changes).slice(0, 200)}` : ''}`
      ).join('\n');
      diffStr = `Dibanding refresh sebelumnya (${diff.prevFetchedAt || '?'}): +${s.added || 0} added, -${s.removed || 0} removed, ~${s.changed || 0} changed\n${items}`;
    }
  }
  let histStr = '-';
  if (createdHist && createdHist.items) {
    const h = createdHist;
    const hl = h.items.map(i =>
      `${(i.firstSeen || '').slice(0, 16)} | ${i.service} | ${i.resourceId || i.key} ${i.label ? `"${i.label}"` : ''}`
      + `${i.createdBy ? ` | dibuat oleh ${i.createdBy} (${i.createdEvent || '?'} @ ${i.createdAt || '?'})` : ' | pembuat: tidak ada event CloudTrail'}`
      + `${i.removedAt ? ` | DIHAPUS ${i.removedAt.slice(0, 16)}` : ''}`).join('\n');
    const ch = Object.entries(h.churn || {}).map(([s, c]) => `${s}: +${c.added} dibuat / ${c.removed} sudah dihapus`).join(', ');
    histStr = `Change log sejak refresh pertama ${h.firstRefresh || '?'} (${h.refreshCount} refresh, jendela ${h.days} hari). `
      + `Resource yang sudah ada saat refresh PERTAMA tidak tercatat di sini (lihat kolom created di DAFTAR RESOURCE).\n`
      + `Total ${h.total} resource baru${h.truncated ? ' (ditampilkan 300 terbaru)' : ''}:\n${hl || '-'}`
      + (ch ? `\nResource berumur pendek (otomatis, hanya jumlah): ${ch}` : '');
  }

  return `=== DATA AWS INVENTORY (AKTUAL) ===
Snapshot terakhir : ${lastRefreshed || '-'}
Tab aktif         : ${activeTab || '-'}${query ? ` | Search: "${query}"` : ''}
Total resource    : ${summary?.totalResources ?? allData.length} | Service types: ${Object.keys(bySvc).length}

RESOURCE PER SERVICE: ${svcStr}

PERUBAHAN TERAKHIR (DIFF refresh terakhir saja):
${diffStr}

RIWAYAT RESOURCE DIBUAT (seluruh change log, tanggal = pertama terlihat saat refresh):
${histStr}

Catatan: "dibuat oleh" berasal dari CloudTrail (diambil 2x sehari); bila kosong, event pembuatnya belum terambil.

DAFTAR RESOURCE (service | id | name | region | type | state | created | tags)${rows.length > MAX ? ` - ${MAX} dari ${rows.length}` : ''}:
${list}
==========================================`.trim();
}


// ── Format AWS Cost untuk AI ──────────────────────────────────────────────────
export function formatAWSCostDataForAI({ yearly_bu, monthly_all, prod_month, availableYears, selectedYears }) {
  if (!yearly_bu?.length) return null;

  const selYears = selectedYears || availableYears || [];

  // Total per BU untuk tahun yang dipilih
  const buTotals = {};
  yearly_bu.filter(r => selYears.includes(r.Year) || selYears.length === 0).forEach(r => {
    Object.entries(r).forEach(([k, v]) => {
      if (k !== 'Year') buTotals[k] = (buTotals[k] || 0) + (parseFloat(v) || 0);
    });
  });
  const grandTotal = Object.values(buTotals).reduce((s, v) => s + v, 0);

  const buStr = Object.entries(buTotals)
    .sort((a, b) => b[1] - a[1])
    .map(([bu, v]) => `${bu}: $${v.toLocaleString('en-US', {minimumFractionDigits:2,maximumFractionDigits:2})} (${grandTotal>0?(v/grandTotal*100).toFixed(1):0}%)`)
    .join('\n');

  // Top products
  const prodTotals = {};
  (prod_month || []).filter(r => selYears.includes(r.Year) || selYears.length === 0).forEach(r => {
    const p = r['Product Code'];
    if (p) prodTotals[p] = (prodTotals[p] || 0) + (parseFloat(r.USD) || 0);
  });
  const topProds = Object.entries(prodTotals)
    .sort((a, b) => b[1] - a[1]).slice(0, 10)
    .map(([p, v]) => `${p}: $${v.toLocaleString('en-US', {minimumFractionDigits:2,maximumFractionDigits:2})}`)
    .join('\n');

  // Monthly trend
  const monthMap = {};
  (monthly_all || []).filter(r => selYears.includes(r.Year) || selYears.length === 0).forEach(r => {
    const k = `${r.Year}-${String(r.MonthNum).padStart(2,'0')}`;
    monthMap[k] = (monthMap[k] || 0) + (parseFloat(r.USD) || 0);
  });
  const monthTrend = Object.entries(monthMap).sort().slice(-6)
    .map(([k, v]) => `${k}: $${v.toLocaleString('en-US', {minimumFractionDigits:2,maximumFractionDigits:2})}`)
    .join(', ');

  return `=== DATA AWS COST EXPLORER (AKTUAL) ===
Tahun Tersedia : ${(availableYears || []).join(', ')}
Tahun Dipilih  : ${selYears.join(', ') || 'Semua'}

RINGKASAN:
- Grand Total  : $${grandTotal.toLocaleString('en-US', {minimumFractionDigits:2,maximumFractionDigits:2})}

BIAYA PER BUSINESS UNIT:
${buStr || '-'}

TOP 10 AWS SERVICES/PRODUCTS:
${topProds || '-'}

TREN 6 BULAN TERAKHIR:
${monthTrend || '-'}
==========================================`.trim();
}
